// File: pages/CompanySettingsPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Building, Mail, Phone, MapPin, Globe, Upload, Save,
  X, RefreshCw, Trash2, Plus, AlertCircle, CheckCircle,
  Image as ImageIcon, Clock, DollarSign, Settings as SettingsIcon,
  Shield, Bell, Linkedin, Twitter, Facebook, Instagram
} from 'lucide-react';
import { companySettingsController } from '../../controllers/companySettingsController';
import { authController } from '../../controllers/authController';

// ─── Default state ─────────────────────────────────────────
const DEFAULT_SETTINGS = {
  companyName: '', legalName: '', registrationNumber: '', taxId: '',
  industry: '', companySize: '', establishedYear: new Date().getFullYear(),
  description: '', email: '', phone: '', fax: '', website: '',
  address:     { street: '', city: '', state: '', country: 'India', zipCode: '' },
  socialMedia: { linkedin: '', twitter: '', facebook: '', instagram: '' },
  timezone: 'Asia/Kolkata', dateFormat: 'DD/MM/YYYY', timeFormat: '24',
  currency: 'INR', language: 'en', fiscalYearStart: '04', weekStartDay: 'monday',
  logo: '', favicon: '',
  primaryColor: '#3b82f6', secondaryColor: '#8b5cf6', accentColor: '#10b981',
  enableMultiCurrency: false, enableTax: true, taxRate: 18,
  enableInventory: true, enableInvoicing: true,
  emailNotifications: true, smsNotifications: false,
  pushNotifications: true, notificationSound: true,
  twoFactorAuth: false, passwordExpiry: 90, sessionTimeout: 30,
  ipWhitelist: [], allowedDomains: []
};

// ─── Tab map ───────────────────────────────────────────────
const TABS = [
  { id: 'general',       label: 'General',       Icon: Building      },
  { id: 'contact',       label: 'Contact',        Icon: MapPin        },
  { id: 'preferences',   label: 'Preferences',    Icon: SettingsIcon  },
  { id: 'branding',      label: 'Branding',       Icon: ImageIcon     },
  { id: 'business',      label: 'Business',       Icon: DollarSign    },
  { id: 'notifications', label: 'Notifications',  Icon: Bell          },
  { id: 'security',      label: 'Security',       Icon: Shield        },
];

// ─── Section-wise save map ─────────────────────────────────
const SECTION_FIELDS = {
  general:       ['companyName','legalName','registrationNumber','taxId','industry','companySize','establishedYear','description'],
  contact:       ['email','phone','fax','website','address','socialMedia'],
  preferences:   ['timezone','dateFormat','timeFormat','currency','language','fiscalYearStart','weekStartDay'],
  business:      ['enableMultiCurrency','enableTax','taxRate','enableInventory','enableInvoicing'],
  notifications: ['emailNotifications','smsNotifications','pushNotifications','notificationSound'],
  security:      ['twoFactorAuth','passwordExpiry','sessionTimeout','ipWhitelist','allowedDomains'],
};

// ─── Styles — defined OUTSIDE so they never re-create ─────
const inputStyle = {
  padding: '10px 12px',
  border: '2px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '14px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  color: '#1e293b',
  background: '#fff',
};

const labelStyle = {
  fontSize: '13px',
  fontWeight: '600',
  color: '#475569',
  marginBottom: '6px',
  display: 'block',
};

// ─── Switch — OUTSIDE main component ──────────────────────
// FIX: Was defined inside CompanySettingsPage, causing remount on every
//      render and losing input focus. Moved outside so identity is stable.
const Switch = ({ value, onChange }) => (
  <div
    onClick={() => onChange(!value)}
    style={{
      width: '48px', height: '26px', borderRadius: '13px',
      background: value ? '#3b82f6' : '#cbd5e1',
      cursor: 'pointer', position: 'relative',
      transition: 'background 0.2s', flexShrink: 0,
    }}
  >
    <div style={{
      position: 'absolute', top: '3px',
      left: value ? '22px' : '3px',
      width: '20px', height: '20px',
      borderRadius: '50%', background: '#fff',
      transition: 'left 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </div>
);

// ─── SwitchRow — OUTSIDE main component ───────────────────
// FIX: Receives `settings` and `set` as props instead of closing over them.
const SwitchRow = ({ label, hint, field, settings, set }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px', background: '#f8fafc', borderRadius: '8px', marginBottom: '12px',
  }}>
    <div>
      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{label}</div>
      {hint && <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{hint}</div>}
    </div>
    <Switch value={settings[field]} onChange={(val) => set(field, val)} />
  </div>
);

// ─── Field — OUTSIDE main component ───────────────────────
// FIX: Was defined inside, causing React to treat it as a new component type
//      on every render, unmounting/remounting children (inputs) and losing focus.
const Field = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
const CompanySettingsPage = () => {
  const [currentUser,    setCurrentUser]    = useState(null);
  const [activeTab,      setActiveTab]      = useState('general');
  const [settings,       setSettings]       = useState(DEFAULT_SETTINGS);
  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);
  const [logoFile,       setLogoFile]       = useState(null);
  const [faviconFile,    setFaviconFile]    = useState(null);
  const [logoPreview,    setLogoPreview]    = useState('');
  const [faviconPreview, setFaviconPreview] = useState('');
  const [toast,          setToast]          = useState(null);
  const [newIP,          setNewIP]          = useState('');
  const [newDomain,      setNewDomain]      = useState('');

  // ─── Toast helper ────────────────────────────────────────
  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ─── Load settings on mount ──────────────────────────────
  useEffect(() => {
    setCurrentUser(authController.getCurrentUser());
    (async () => {
      setLoading(true);
      const res = await companySettingsController.getSettings();
      if (res.success && res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
        setLogoPreview(res.data.logo || '');
        setFaviconPreview(res.data.favicon || '');
      }
      setLoading(false);
    })();
  }, []);

  // ─── Field change helpers ────────────────────────────────
  const set = useCallback((field, value) =>
    setSettings(prev => ({ ...prev, [field]: value })), []);

  const setNested = useCallback((parent, field, value) =>
    setSettings(prev => ({ ...prev, [parent]: { ...prev[parent], [field]: value } })), []);

  // ─── Logo / favicon file pick ────────────────────────────
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleFaviconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFaviconFile(file);
    setFaviconPreview(URL.createObjectURL(file));
  };

  // ─── Save — section-wise ─────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      let res;

      if (activeTab === 'branding') {
        const brandData = {
          primaryColor:   settings.primaryColor,
          secondaryColor: settings.secondaryColor,
          accentColor:    settings.accentColor,
        };
        res = await companySettingsController.updateBranding(brandData, logoFile, faviconFile);
        if (res.success) {
          setLogoPreview(res.data?.logo || logoPreview);
          setFaviconPreview(res.data?.favicon || faviconPreview);
          setSettings(prev => ({
            ...prev,
            logo:    res.data?.logo    || prev.logo,
            favicon: res.data?.favicon || prev.favicon,
          }));
          setLogoFile(null);
          setFaviconFile(null);
        }
      } else if (SECTION_FIELDS[activeTab]) {
        const sectionData = {};
        SECTION_FIELDS[activeTab].forEach(f => { sectionData[f] = settings[f]; });

        const fnMap = {
          general:       () => companySettingsController.updateGeneralInfo(sectionData),
          contact:       () => companySettingsController.updateContactInfo(sectionData),
          preferences:   () => companySettingsController.updatePreferences(sectionData),
          business:      () => companySettingsController.updateBusinessSettings(sectionData),
          notifications: () => companySettingsController.updateNotificationSettings(sectionData),
          security:      () => companySettingsController.updateSecuritySettings(sectionData),
        };
        res = await fnMap[activeTab]();
      }

      if (res?.success) {
        if (res.data) setSettings(prev => ({ ...prev, ...res.data }));
        showToast('success', 'Settings saved successfully!');
      } else {
        showToast('error', res?.error || 'Failed to save settings');
      }
    } catch (err) {
      showToast('error', err.message || 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  // ─── Reset ───────────────────────────────────────────────
  const handleReset = async () => {
    if (!window.confirm('Reset all settings to default?')) return;
    const res = await companySettingsController.resetToDefaults();
    if (res.success) {
      setSettings(prev => ({ ...prev, ...res.data }));
      showToast('success', 'Settings reset to default');
    } else {
      showToast('error', res.error || 'Reset failed');
    }
  };

  // ─── Logo delete ─────────────────────────────────────────
  const handleDeleteLogo = async () => {
    const res = await companySettingsController.deleteLogo();
    if (res.success) {
      setSettings(prev => ({ ...prev, logo: '' }));
      setLogoPreview('');
      setLogoFile(null);
      showToast('success', 'Logo removed');
    } else showToast('error', res.error || 'Failed to remove logo');
  };

  const handleDeleteFavicon = async () => {
    const res = await companySettingsController.deleteFavicon();
    if (res.success) {
      setSettings(prev => ({ ...prev, favicon: '' }));
      setFaviconPreview('');
      setFaviconFile(null);
      showToast('success', 'Favicon removed');
    } else showToast('error', res.error || 'Failed to remove favicon');
  };

  // ─── IP & Domain ─────────────────────────────────────────
  const handleAddIP = async () => {
    if (!newIP.trim()) return;
    const res = await companySettingsController.addIPToWhitelist(newIP.trim(), settings.ipWhitelist);
    if (res.success) {
      setSettings(prev => ({ ...prev, ipWhitelist: res.data?.ipWhitelist || [...prev.ipWhitelist, newIP.trim()] }));
      setNewIP('');
    } else showToast('error', res.error || 'Failed to add IP');
  };

  const handleRemoveIP = async (ip) => {
    const res = await companySettingsController.removeIPFromWhitelist(ip, settings.ipWhitelist);
    if (res.success) {
      setSettings(prev => ({ ...prev, ipWhitelist: prev.ipWhitelist.filter(i => i !== ip) }));
    } else showToast('error', res.error || 'Failed to remove IP');
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    const res = await companySettingsController.addAllowedDomain(newDomain.trim(), settings.allowedDomains);
    if (res.success) {
      setSettings(prev => ({ ...prev, allowedDomains: res.data?.allowedDomains || [...prev.allowedDomains, newDomain.trim()] }));
      setNewDomain('');
    } else showToast('error', res.error || 'Failed to add domain');
  };

  const handleRemoveDomain = async (domain) => {
    const res = await companySettingsController.removeAllowedDomain(domain, settings.allowedDomains);
    if (res.success) {
      setSettings(prev => ({ ...prev, allowedDomains: prev.allowedDomains.filter(d => d !== domain) }));
    } else showToast('error', res.error || 'Failed to remove domain');
  };

  // ─── Access guard ─────────────────────────────────────────
  if (currentUser && currentUser.role !== 'super_admin' && currentUser.role !== 'admin') {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ background: '#fee2e2', border: '2px solid #ef4444', borderRadius: '12px', padding: '24px', display: 'flex', gap: '16px', alignItems: 'center' }}>
          <AlertCircle size={32} color="#dc2626" />
          <div>
            <h3 style={{ margin: 0, color: '#991b1b' }}>Access Denied</h3>
            <p style={{ margin: '4px 0 0', color: '#7f1d1d', fontSize: '14px' }}>Only Super Admins and Admins can access Company Settings.</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Loading ──────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: '16px' }}>
        <RefreshCw size={32} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#64748b', fontSize: '16px' }}>Loading settings...</p>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════
  return (
    <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh' }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 9999, background: toast.type === 'success' ? '#10b981' : '#ef4444', color: '#fff', padding: '14px 20px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', fontSize: '14px', fontWeight: '500' }}>
          {toast.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Building size={32} color="#3b82f6" /> Company Settings
        </h1>
        <p style={{ color: '#64748b', marginTop: '6px', fontSize: '15px' }}>Manage your company information, preferences, and configurations</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '24px' }}>

        {/* Sidebar */}
        <div style={{ background: '#fff', borderRadius: '12px', padding: '12px', height: 'fit-content', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', position: 'sticky', top: '24px' }}>
          {TABS.map(({ id, label, Icon }) => (
            <div
              key={id}
              onClick={() => setActiveTab(id)}
              style={{ padding: '11px 14px', display: 'flex', alignItems: 'center', gap: '10px', borderRadius: '8px', cursor: 'pointer', marginBottom: '2px', fontSize: '14px', fontWeight: '500', color: activeTab === id ? '#3b82f6' : '#64748b', background: activeTab === id ? '#eff6ff' : 'transparent', transition: 'all 0.15s' }}
            >
              <Icon size={17} /> {label}
            </div>
          ))}

          <div style={{ borderTop: '1px solid #e2e8f0', marginTop: '12px', paddingTop: '12px' }}>
            <div onClick={handleReset} style={{ padding: '11px 14px', display: 'flex', alignItems: 'center', gap: '10px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#ef4444', transition: 'all 0.15s' }}>
              <RefreshCw size={17} /> Reset Defaults
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ background: '#fff', borderRadius: '12px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>

          {/* ── GENERAL ────────────────────────────────────── */}
          {activeTab === 'general' && (
            <>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginTop: 0, marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' }}>General Information</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <Field label="Company Name *">
                  <input
                    style={inputStyle}
                    value={settings.companyName}
                    onChange={e => set('companyName', e.target.value)}
                    placeholder="Enter company name"
                  />
                </Field>
                <Field label="Legal Name">
                  <input
                    style={inputStyle}
                    value={settings.legalName}
                    onChange={e => set('legalName', e.target.value)}
                    placeholder="Enter legal name"
                  />
                </Field>
                <Field label="Registration Number">
                  <input
                    style={inputStyle}
                    value={settings.registrationNumber}
                    onChange={e => set('registrationNumber', e.target.value)}
                    placeholder="CIN / Reg No."
                  />
                </Field>
                <Field label="Tax ID / GSTIN">
                  <input
                    style={inputStyle}
                    value={settings.taxId}
                    onChange={e => set('taxId', e.target.value)}
                    placeholder="GST number"
                  />
                </Field>
                <Field label="Industry">
                  <select
                    style={inputStyle}
                    value={settings.industry}
                    onChange={e => set('industry', e.target.value)}
                  >
                    <option value="">Select industry</option>
                    {['Technology','Finance','Healthcare','Retail','Manufacturing','Education'].map(i => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Company Size">
                  <select
                    style={inputStyle}
                    value={settings.companySize}
                    onChange={e => set('companySize', e.target.value)}
                  >
                    <option value="">Select size</option>
                    {['1-10','11-50','51-200','201-500','500+'].map(s => (
                      <option key={s} value={s}>{s} employees</option>
                    ))}
                  </select>
                </Field>
                <Field label="Established Year">
                  <input
                    type="number"
                    style={inputStyle}
                    value={settings.establishedYear}
                    onChange={e => set('establishedYear', parseInt(e.target.value))}
                    min="1800"
                    max={new Date().getFullYear()}
                  />
                </Field>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="Description">
                    <textarea
                      style={{ ...inputStyle, minHeight: '90px', resize: 'vertical', fontFamily: 'inherit' }}
                      value={settings.description}
                      onChange={e => set('description', e.target.value)}
                      placeholder="Brief description of your company"
                    />
                  </Field>
                </div>
              </div>
            </>
          )}

          {/* ── CONTACT ────────────────────────────────────── */}
          {activeTab === 'contact' && (
            <>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginTop: 0, marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' }}>Contact Information</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <Field label="Email">
                  <input type="email" style={inputStyle} value={settings.email} onChange={e => set('email', e.target.value)} placeholder="info@company.com" />
                </Field>
                <Field label="Phone">
                  <input type="tel" style={inputStyle} value={settings.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 00000 00000" />
                </Field>
                <Field label="Fax">
                  <input type="tel" style={inputStyle} value={settings.fax} onChange={e => set('fax', e.target.value)} placeholder="Fax number" />
                </Field>
                <Field label="Website">
                  <input type="url" style={inputStyle} value={settings.website} onChange={e => set('website', e.target.value)} placeholder="https://yourcompany.com" />
                </Field>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="Street Address">
                    <input style={inputStyle} value={settings.address.street} onChange={e => setNested('address', 'street', e.target.value)} placeholder="Street address" />
                  </Field>
                </div>
                <Field label="City">
                  <input style={inputStyle} value={settings.address.city} onChange={e => setNested('address', 'city', e.target.value)} />
                </Field>
                <Field label="State">
                  <input style={inputStyle} value={settings.address.state} onChange={e => setNested('address', 'state', e.target.value)} />
                </Field>
                <Field label="Country">
                  <input style={inputStyle} value={settings.address.country} onChange={e => setNested('address', 'country', e.target.value)} />
                </Field>
                <Field label="ZIP / PIN Code">
                  <input style={inputStyle} value={settings.address.zipCode} onChange={e => setNested('address', 'zipCode', e.target.value)} />
                </Field>
              </div>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', margin: '28px 0 16px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>Social Media</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <Field label="LinkedIn">
                  <input type="url" style={inputStyle} value={settings.socialMedia.linkedin} onChange={e => setNested('socialMedia', 'linkedin', e.target.value)} placeholder="linkedin.com/company/..." />
                </Field>
                <Field label="Twitter">
                  <input type="url" style={inputStyle} value={settings.socialMedia.twitter} onChange={e => setNested('socialMedia', 'twitter', e.target.value)} placeholder="twitter.com/..." />
                </Field>
                <Field label="Facebook">
                  <input type="url" style={inputStyle} value={settings.socialMedia.facebook} onChange={e => setNested('socialMedia', 'facebook', e.target.value)} placeholder="facebook.com/..." />
                </Field>
                <Field label="Instagram">
                  <input type="url" style={inputStyle} value={settings.socialMedia.instagram} onChange={e => setNested('socialMedia', 'instagram', e.target.value)} placeholder="instagram.com/..." />
                </Field>
              </div>
            </>
          )}

          {/* ── PREFERENCES ────────────────────────────────── */}
          {activeTab === 'preferences' && (
            <>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginTop: 0, marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' }}>System Preferences</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <Field label="Timezone">
                  <select style={inputStyle} value={settings.timezone} onChange={e => set('timezone', e.target.value)}>
                    <option value="Asia/Kolkata">India (IST)</option>
                    <option value="Asia/Dubai">Dubai (GST)</option>
                    <option value="Europe/London">UK (GMT)</option>
                    <option value="America/New_York">US Eastern</option>
                    <option value="America/Los_Angeles">US Pacific</option>
                  </select>
                </Field>
                <Field label="Language">
                  <select style={inputStyle} value={settings.language} onChange={e => set('language', e.target.value)}>
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                  </select>
                </Field>
                <Field label="Date Format">
                  <select style={inputStyle} value={settings.dateFormat} onChange={e => set('dateFormat', e.target.value)}>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </Field>
                <Field label="Time Format">
                  <select style={inputStyle} value={settings.timeFormat} onChange={e => set('timeFormat', e.target.value)}>
                    <option value="24">24 hour</option>
                    <option value="12">12 hour</option>
                  </select>
                </Field>
                <Field label="Currency">
                  <select style={inputStyle} value={settings.currency} onChange={e => set('currency', e.target.value)}>
                    <option value="INR">INR — ₹ Indian Rupee</option>
                    <option value="USD">USD — $ US Dollar</option>
                    <option value="EUR">EUR — € Euro</option>
                    <option value="GBP">GBP — £ British Pound</option>
                  </select>
                </Field>
                <Field label="Fiscal Year Start">
                  <select style={inputStyle} value={settings.fiscalYearStart} onChange={e => set('fiscalYearStart', e.target.value)}>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i} value={String(i + 1).padStart(2, '0')}>
                        {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Week Starts On">
                  <select style={inputStyle} value={settings.weekStartDay} onChange={e => set('weekStartDay', e.target.value)}>
                    <option value="monday">Monday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                </Field>
              </div>
            </>
          )}

          {/* ── BRANDING ───────────────────────────────────── */}
          {activeTab === 'branding' && (
            <>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginTop: 0, marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' }}>Branding & Theme</h2>

              {/* Logo */}
              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Company Logo</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', border: '2px dashed #e2e8f0', borderRadius: '10px', background: '#f8fafc' }}>
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo" style={{ width: '100px', height: '60px', objectFit: 'contain', borderRadius: '6px', background: '#fff', padding: '4px', border: '1px solid #e2e8f0' }} />
                  ) : (
                    <div style={{ width: '100px', height: '60px', background: '#e2e8f0', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ImageIcon size={24} color="#94a3b8" />
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ padding: '8px 16px', background: '#3b82f6', color: '#fff', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Upload size={14} /> Upload Logo
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoChange} />
                    </label>
                    {logoPreview && (
                      <button onClick={handleDeleteLogo} style={{ padding: '8px 16px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Trash2 size={14} /> Remove
                      </button>
                    )}
                  </div>
                  <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0 }}>PNG, SVG, WebP. Max 5MB</p>
                </div>
              </div>

              {/* Favicon */}
              <div style={{ marginBottom: '28px' }}>
                <label style={labelStyle}>Favicon</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', border: '2px dashed #e2e8f0', borderRadius: '10px', background: '#f8fafc' }}>
                  {faviconPreview ? (
                    <img src={faviconPreview} alt="Favicon" style={{ width: '48px', height: '48px', objectFit: 'contain', borderRadius: '6px', background: '#fff', padding: '4px', border: '1px solid #e2e8f0' }} />
                  ) : (
                    <div style={{ width: '48px', height: '48px', background: '#e2e8f0', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ImageIcon size={20} color="#94a3b8" />
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ padding: '8px 16px', background: '#3b82f6', color: '#fff', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Upload size={14} /> Upload Favicon
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFaviconChange} />
                    </label>
                    {faviconPreview && (
                      <button onClick={handleDeleteFavicon} style={{ padding: '8px 16px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Trash2 size={14} /> Remove
                      </button>
                    )}
                  </div>
                  <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0 }}>ICO, PNG. Max 1MB</p>
                </div>
              </div>

              {/* Colors */}
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', margin: '0 0 16px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>Brand Colors</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                {[
                  { label: 'Primary Color',   field: 'primaryColor'   },
                  { label: 'Secondary Color', field: 'secondaryColor' },
                  { label: 'Accent Color',    field: 'accentColor'    },
                ].map(({ label, field }) => (
                  <Field key={field} label={label}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', border: '2px solid #e2e8f0', borderRadius: '8px' }}>
                      <input
                        type="color"
                        value={settings[field]}
                        onChange={e => set(field, e.target.value)}
                        style={{ width: '32px', height: '32px', border: 'none', padding: 0, background: 'none', cursor: 'pointer', borderRadius: '4px' }}
                      />
                      <input
                        type="text"
                        value={settings[field]}
                        onChange={e => set(field, e.target.value)}
                        style={{ border: 'none', outline: 'none', fontSize: '13px', flex: 1, color: '#1e293b', background: 'none' }}
                      />
                    </div>
                  </Field>
                ))}
              </div>
            </>
          )}

          {/* ── BUSINESS ───────────────────────────────────── */}
          {activeTab === 'business' && (
            <>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginTop: 0, marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' }}>Business Settings</h2>
              <SwitchRow label="Multi-Currency Support" hint="Accept payments in multiple currencies" field="enableMultiCurrency" settings={settings} set={set} />
              <SwitchRow label="Tax Enabled"            hint="Apply taxes on invoices and transactions" field="enableTax"           settings={settings} set={set} />
              {settings.enableTax && (
                <div style={{ marginBottom: '12px', paddingLeft: '4px' }}>
                  <Field label="Default Tax Rate (%)">
                    <input
                      type="number"
                      style={{ ...inputStyle, maxWidth: '160px' }}
                      value={settings.taxRate}
                      onChange={e => set('taxRate', parseFloat(e.target.value))}
                      min="0" max="100" step="0.01"
                    />
                  </Field>
                </div>
              )}
              <SwitchRow label="Inventory Management" hint="Track stock and product inventory"     field="enableInventory"  settings={settings} set={set} />
              <SwitchRow label="Invoicing"            hint="Enable invoice creation and management" field="enableInvoicing"  settings={settings} set={set} />
            </>
          )}

          {/* ── NOTIFICATIONS ──────────────────────────────── */}
          {activeTab === 'notifications' && (
            <>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginTop: 0, marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' }}>Notification Preferences</h2>
              <SwitchRow label="Email Notifications" hint="Receive alerts via email"           field="emailNotifications" settings={settings} set={set} />
              <SwitchRow label="SMS Notifications"   hint="Receive alerts via SMS"             field="smsNotifications"   settings={settings} set={set} />
              <SwitchRow label="Push Notifications"  hint="Receive browser push alerts"        field="pushNotifications"  settings={settings} set={set} />
              <SwitchRow label="Notification Sound"  hint="Play sound on new notifications"    field="notificationSound"  settings={settings} set={set} />
            </>
          )}

          {/* ── SECURITY ───────────────────────────────────── */}
          {activeTab === 'security' && (
            <>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginTop: 0, marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' }}>Security Settings</h2>
              <SwitchRow label="Two-Factor Authentication" hint="Require 2FA for all admin users" field="twoFactorAuth" settings={settings} set={set} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px' }}>
                <Field label="Password Expiry (days)">
                  <input
                    type="number"
                    style={inputStyle}
                    value={settings.passwordExpiry}
                    onChange={e => set('passwordExpiry', parseInt(e.target.value))}
                    min="0"
                    placeholder="90 (0 = never)"
                  />
                </Field>
                <Field label="Session Timeout (minutes)">
                  <input
                    type="number"
                    style={inputStyle}
                    value={settings.sessionTimeout}
                    onChange={e => set('sessionTimeout', parseInt(e.target.value))}
                    min="5"
                  />
                </Field>
              </div>

              {/* IP Whitelist */}
              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>IP Whitelist</label>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <input
                    style={{ ...inputStyle, flex: 1 }}
                    value={newIP}
                    onChange={e => setNewIP(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddIP()}
                    placeholder="e.g. 192.168.1.1"
                  />
                  <button
                    onClick={handleAddIP}
                    style={{ padding: '10px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
                  >
                    <Plus size={15} /> Add IP
                  </button>
                </div>
                {settings.ipWhitelist.map((ip, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f8fafc', borderRadius: '8px', marginBottom: '6px', fontSize: '14px', color: '#1e293b' }}>
                    <span style={{ fontFamily: 'monospace' }}>{ip}</span>
                    <button onClick={() => handleRemoveIP(ip)} style={{ padding: '4px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Remove</button>
                  </div>
                ))}
                {settings.ipWhitelist.length === 0 && (
                  <p style={{ color: '#94a3b8', fontSize: '13px', margin: '4px 0 0' }}>No IPs added — all IPs are allowed</p>
                )}
              </div>

              {/* Allowed Domains */}
              <div>
                <label style={labelStyle}>Allowed Domains</label>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <input
                    style={{ ...inputStyle, flex: 1 }}
                    value={newDomain}
                    onChange={e => setNewDomain(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddDomain()}
                    placeholder="e.g. company.com"
                  />
                  <button
                    onClick={handleAddDomain}
                    style={{ padding: '10px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
                  >
                    <Plus size={15} /> Add Domain
                  </button>
                </div>
                {settings.allowedDomains.map((domain, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f8fafc', borderRadius: '8px', marginBottom: '6px', fontSize: '14px', color: '#1e293b' }}>
                    <span style={{ fontFamily: 'monospace' }}>{domain}</span>
                    <button onClick={() => handleRemoveDomain(domain)} style={{ padding: '4px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Remove</button>
                  </div>
                ))}
                {settings.allowedDomains.length === 0 && (
                  <p style={{ color: '#94a3b8', fontSize: '13px', margin: '4px 0 0' }}>No domains restricted</p>
                )}
              </div>
            </>
          )}

          {/* ── Action Bar ─────────────────────────────────── */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px', paddingTop: '24px', borderTop: '2px solid #e2e8f0' }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ padding: '11px 28px', background: saving ? '#93c5fd' : '#3b82f6', color: '#fff', border: 'none', borderRadius: '9px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', transition: 'background 0.2s' }}
            >
              {saving
                ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                : <Save size={16} />
              }
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default CompanySettingsPage;